import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
/* eslint-disable max-len */
import { useCallback, useContext, useEffect, useMemo, useId, } from 'preact/compat';
import { useController, useFormContext, } from 'react-hook-form';
import { Swiper, SwiperSlide } from 'swiper/react';
import NoSessions from '@widgets/GenericBookings/components/SessionSelector/components/NoSessions';
import { useTimeConflict } from '@bookingForm/context/TimeConflicts';
import { useSessionData } from '@widgets/GenericBookings/context/SessionData';
import { useMessage } from '@genericBookings/context/Message';
import { useAppDispatch } from '@genericBookings/store/hooks';
import { setActivities, setTheActiveSessionForActivity, setTheSessionsForActivity, } from '@genericBookings/store/reducers/activities';
import { DummyLink } from '@genericBookings/components/SelectedOption/styles';
import { useCourse } from '@bookingForm/context/Course';
import Session from './components/Session';
import Header from './components/Header';
import { defaultSwiperArgs, getRestyledSwiperArgs } from './defaults';
import Styles from './styles';
function SessionSelector(props) {
    const { name: fieldName, restyled, options, activity: activityWithSessions, showPriceOnBands, productId, listView = false, } = props;
    const { activity, sessions, activeSession } = activityWithSessions;
    const { id: activityId } = activity;
    const { control, } = useFormContext();
    const { field: { onChange } } = useController({
        name: fieldName,
        control,
    });
    const { addMessage, activityReset, } = useMessage();
    const dispatch = useAppDispatch();
    const swiperId = useId();
    const { chooseSessionTitle, chooseSessionsCopy, analyticsTitle, analyticsUrl, analyticsPath, noSessionsText, allowOverlaps, isBuilder, isMulti, debug, showSessionAvailability, } = useContext(options);
    const { addSelectedTimePeriod, checkTimeConflicts, convertActivityToSession, } = useTimeConflict();
    const { updateActivity } = useSessionData();
    const { setIsCourse } = useCourse();
    // const [isRequestingData, setIsRequestingData] = useState<boolean>(false);
    const swiperArgs = useMemo(() => ((restyled) ? getRestyledSwiperArgs(swiperId) : defaultSwiperArgs), [restyled, swiperId]);
    const timeConflictOptions = useCallback((session) => {
        if (activity.durationInMinutes) {
            return convertActivityToSession({
                ...activityWithSessions,
                activeSession: session,
            });
        }
        return null;
    }, [activity.durationInMinutes, convertActivityToSession, activityWithSessions]);
    const handleChange = (ev, session) => {
        onChange(parseFloat(ev.currentTarget.value));
        const timeOptions = timeConflictOptions(session);
        if (timeOptions && !allowOverlaps) {
            addSelectedTimePeriod(timeOptions);
        }
        activityReset(activityId);
        if (session.Warning) {
            addMessage({
                type: 'warning',
                text: session.Warning ?? '',
                closable: false,
                sessionId: session.SessionId,
                activityId,
            });
        }
        if (session.Error) {
            addMessage({
                type: 'error',
                text: session.Error ?? '',
                closable: false,
                sessionId: session.SessionId,
                activityId,
            });
        }
        setIsCourse(session.Course ?? false);
        dispatch(setTheActiveSessionForActivity(activityWithSessions, session));
    };
    const handleDisabledSessionClick = useCallback((newActivityId, session) => {
        const overwrittenActivities = updateActivity(newActivityId, session);
        if (isMulti) {
            dispatch(setActivities(overwrittenActivities));
        }
    }, [updateActivity, isMulti, dispatch]);
    const handleSessionSelected = useCallback(() => {
        if (activeSession !== null) {
            try {
                // to do, remove this.
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'page_view', {
                        page_title: `${analyticsTitle} - Choose time`,
                        page_location: `${analyticsUrl}step-3`,
                        page_path: `${analyticsPath}step-3`,
                        send_to: 'UA-44012490-1',
                    });
                }
            }
            catch (e) {
                // eslint-disable-next-line no-console
                console.warn(e.message);
            }
        }
    }, [analyticsPath, analyticsTitle, analyticsUrl, activeSession]);
    const handleDebugReset = useCallback(() => {
        if (debug) {
            dispatch(setTheSessionsForActivity([], activityWithSessions));
        }
    }, [activityWithSessions, debug, dispatch]);
    const renderSlides = () => {
        if (!sessions || !sessions.length) {
            return _jsx(NoSessions, { options: options, noSessionsText: noSessionsText ?? 'No Sessions Found' });
        }
        return sessions.map((session) => (_jsx(SwiperSlide, { id: `session-${session.SessionId}-wrapper`, children: _jsx(Session, { onChange: (ev) => handleChange(ev, session), session: session, isSelected: session.SessionId === activeSession?.SessionId, isDisabled: !allowOverlaps && checkTimeConflicts(session, activity), activityId: activityId, disabledClick: handleDisabledSessionClick, showPriceOnBands: showPriceOnBands, showSessionAvailability: showSessionAvailability, productId: productId }) }, session.SessionId)));
    };
    const renderListItems = () => {
        if (!sessions || !sessions.length) {
            return _jsx(NoSessions, { options: options, noSessionsText: noSessionsText ?? 'No Sessions Found' });
        }
        return (_jsx(Styles.List, { children: sessions.map((session) => (_jsx(Styles.ListItem, { children: _jsx(Session, { onChange: (ev) => handleChange(ev, session), session: session, isSelected: session.SessionId === activeSession?.SessionId, isDisabled: !allowOverlaps && checkTimeConflicts(session, activity), activityId: activityId, disabledClick: handleDisabledSessionClick, showPriceOnBands: showPriceOnBands, showSessionAvailability: showSessionAvailability, productId: productId }) }, session.SessionId))) }));
    };
    useEffect(handleSessionSelected, [activeSession, handleSessionSelected]);
    if (!sessions)
        return null;
    return (_jsxs(Styles.Wrapper, { children: [_jsx("input", { type: "hidden", name: fieldName, value: activeSession?.SessionId }), _jsx(Header, { title: chooseSessionTitle ?? null, copy: chooseSessionsCopy }), listView ? (_jsxs(Styles.ListWrapper, { children: [renderListItems(), !isBuilder && sessions.length > 0 && debug && (_jsx(DummyLink, { onClick: () => handleDebugReset(), children: "Reset Sessions" }))] })) : (_jsxs(Styles.SwiperWrapper, { id: `sessions-wrapper-${swiperId}`, children: [restyled
                        && (_jsxs(Styles.SwiperNavigation, { children: [_jsx(Styles.SwiperNext, { type: "button", className: "swiper-button-prev" }), _jsx(Styles.SwiperPrev, { type: "button", className: "swiper-button-next" })] })), _jsx(Swiper, { ...swiperArgs, ref: null, children: renderSlides() }), (!isBuilder && sessions.length > 0 && debug) && (_jsx(DummyLink, { onClick: () => handleDebugReset(), children: "Reset Sessions" }))] }))] }));
}
SessionSelector.defaultProps = {
    restyled: false,
    index: undefined,
    prefix: '',
    duration: undefined,
    isRequestingData: false,
    listView: false,
};
export default SessionSelector;
